import { t } from '@lingui/macro'
import { isAfter } from 'date-fns/esm'
import { type FC, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { type DataPoint, DataPointEntry, DataPointList } from '@/modules/shared/components/data-point'
import { getDataPointListValues, getMostRecentDataPoint } from '@/utils/health-parameters'

export type HealthProfileCategoryLayoutProps = {
  /**
   * Informative intro for the health parameter
   */
  infomativeIntro?: string

  /**
   * Data point that the user has entered (via healthblocks)
   */
  userDataPoint?: DataPoint

  /**
   * Data points that are synops validated (fetched from synops)
   */
  synopsDataPoints?: DataPoint[]

  /**
   * Empty data point label
   */
  emptyDataPointLabel?: string

  /**
   * Whether to show the description text (containing timestamps) above data points.
   * Defaults to true.
   */
  showDescriptions?: boolean
}

/**
 * Layout for the health profile category
 */
export const HealthProfileCategoryLayout: FC<HealthProfileCategoryLayoutProps> = ({
  infomativeIntro,
  userDataPoint,
  synopsDataPoints,
  emptyDataPointLabel,
  showDescriptions = true,
}) => {
  const theme = useTheme()
  const isUserDataMoreRecent = useMemo(() => {
    if (!userDataPoint) {
      return false
    } else if (!synopsDataPoints) {
      return true
    }

    const mostRecentUserDataPoint = getMostRecentDataPoint([userDataPoint])
    const mostRecentSynopsDataPoint = getMostRecentDataPoint(synopsDataPoints)

    return (
      mostRecentUserDataPoint?.date &&
      mostRecentSynopsDataPoint?.date &&
      isAfter(new Date(mostRecentUserDataPoint.date), new Date(mostRecentSynopsDataPoint.date))
    )
  }, [userDataPoint, synopsDataPoints])

  // Template variables
  // ------------------------------------------------------------------------- /

  if (!emptyDataPointLabel) {
    emptyDataPointLabel = t({
      id: 'health-parameter.empty-data-point',
      comment: 'Label for the empty data point',
      message: 'Geen data beschikbaar',
    })
  }

  // Interaction handlers
  // ------------------------------------------------------------------------- /
  return (
    <FlexColumn gap={theme.UI.SpacingPx.Space6}>
      {/* INFORMATIVE INTRO TO THE HEALTH PARAMETER DATA */}
      {infomativeIntro && showDescriptions ? (
        <FlexColumn gap={theme.UI.SpacingPx.Space2}>
          <Span type='BodyMediumBold'>{infomativeIntro}</Span>
        </FlexColumn>
      ) : null}

      {/* USER DATA POINT  */}
      {userDataPoint && isUserDataMoreRecent ? (
        <DataPointList
          description={
            showDescriptions
              ? t({
                  id: 'health-parameter.user-input',
                  comment: 'Label for the user input data point',
                  message: 'Ingegeven op {date}',
                  values: getDataPointListValues(userDataPoint),
                })
              : undefined
          }
          dataPoints={[userDataPoint]}
        />
      ) : null}

      {/* SYNOPS DATA POINTS */}
      {synopsDataPoints ? (
        <DataPointList
          description={
            showDescriptions
              ? t({
                  id: 'health-parameter.synops-data',
                  comment: 'Label for the synops data',
                  message: 'Ingegeven op {date}',
                  values: getDataPointListValues(getMostRecentDataPoint(synopsDataPoints)),
                })
              : undefined
          }
          dataPoints={synopsDataPoints}
        />
      ) : (
        <FlexColumn gap={theme.UI.SpacingPx.Space4}>
          <DataPointEntry dataPoint={{ label: emptyDataPointLabel }} />
        </FlexColumn>
      )}
    </FlexColumn>
  )
}
